/** InstallAppPrompt — cross-platform "Add to Home Screen" nudge.
 *
 *  UX rules (deliberately conservative — nobody likes install nags):
 *   • Delay 8 seconds after first visit so users see the product first.
 *   • Hide forever if the app is already installed
 *     (detected via `display-mode: standalone` or the `appinstalled` event).
 *   • On dismissal → stash a timestamp in localStorage. Don't re-show for
 *     30 days. On successful install → set a permanent flag; never re-show.
 *   • On Android / desktop Chrome / Edge → capture the native
 *     `beforeinstallprompt` event and expose a real "Install" button that
 *     calls `deferredPrompt.prompt()`. This is the one-click flow.
 *   • On iOS Safari (no install event exists) → render manual instructions
 *     with the actual "Share" icon so users don't need to guess.
 *   • On desktop Safari / unknown browsers → same fallback as iOS.
 */
import { useEffect, useState } from "react";
import { X, Download, Smartphone, Share, Plus } from "lucide-react";

const STORAGE_KEYS = {
  installed: "cs.pwa.installed",       // set once — never shown again
  dismissed: "cs.pwa.dismissed_at",    // ISO timestamp — 30-day cooldown
};
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;    // 7 days — beta phase, we want people back soon
const APPEAR_DELAY_MS = 8_000;                   // 8-sec delay on first visit

function isRunningStandalone() {
  // Chrome/Android/desktop
  if (typeof window !== "undefined"
      && window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari legacy
  if (typeof navigator !== "undefined" && navigator.standalone === true) return true;
  return false;
}

function detectIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad on iOS 13+ reports as MacIntel with maxTouchPoints > 1
  const isIPadOS = ua.includes("Mac") && (navigator.maxTouchPoints || 0) > 1;
  return /iPhone|iPad|iPod/.test(ua) || isIPadOS;
}

export default function InstallAppPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    // Dev/preview trigger — visit any page with ?installPrompt=1 to force
    // the card open (iOS variant if ?ios=1). Bypasses cooldown so you can
    // eyeball the UI on any device without waiting.
    const params = new URLSearchParams(window.location.search);
    if (params.get("installPrompt") === "1") {
      setIos(params.get("ios") === "1" || detectIOS());
      setVisible(true);
      return;
    }

    // Guardrails — don't even mount inside standalone mode.
    if (isRunningStandalone()) return;
    if (localStorage.getItem(STORAGE_KEYS.installed) === "1") return;
    const lastDismiss = localStorage.getItem(STORAGE_KEYS.dismissed);
    if (lastDismiss && Date.now() - Number(lastDismiss) < COOLDOWN_MS) return;

    setIos(detectIOS());

    // Android / Chrome / Edge: capture the browser's native install offer.
    const captureNative = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", captureNative);

    // If the user installs (via any channel), lock the prompt for good.
    const markInstalled = () => {
      localStorage.setItem(STORAGE_KEYS.installed, "1");
      setVisible(false);
    };
    window.addEventListener("appinstalled", markInstalled);

    // For iOS Safari (no beforeinstallprompt), surface the manual guide
    // after the delay so first impressions aren't cluttered.
    let iosTimer;
    if (detectIOS()) {
      iosTimer = setTimeout(() => setVisible(true), APPEAR_DELAY_MS);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", captureNative);
      window.removeEventListener("appinstalled", markInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEYS.dismissed, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem(STORAGE_KEYS.installed, "1");
    } else {
      localStorage.setItem(STORAGE_KEYS.dismissed, String(Date.now()));
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[9999]
                 rounded-2xl border border-white/12 bg-[#0B0B0D]/95 backdrop-blur-xl
                 shadow-[0_18px_60px_rgba(0,0,0,0.55)] p-5 text-white
                 animate-in slide-in-from-bottom-6 fade-in duration-500"
      role="dialog"
      aria-label="Install Call Sheet on your device"
      data-testid="install-pwa-prompt"
    >
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-white/50 hover:text-white transition-colors"
        aria-label="Dismiss"
        data-testid="install-pwa-dismiss"
      >
        <X size={16} />
      </button>

      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl overflow-hidden bg-white/5 shrink-0
                        flex items-center justify-center border border-white/10">
          <img src="/callsheet-round-logo.jpeg" alt=""
               className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0">
          <div className="text-[9px] tracking-[0.28em] uppercase text-white/50">
            Add to home screen
          </div>
          <div className="font-display text-lg leading-tight mt-1">
            Get Call Sheet as an app
          </div>
          <div className="text-xs text-white/60 mt-1.5 leading-relaxed">
            Full-screen, faster, and an icon on your home screen — just like a
            native app.
          </div>
        </div>
      </div>

      {ios ? (
        <div className="mt-4 space-y-2">
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">
            iPhone / iPad — 2 taps
          </div>
          <ol className="text-xs text-white/80 space-y-2 leading-relaxed">
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-white/8 flex items-center
                               justify-center text-[10px] font-mono">1</span>
              Tap
              <Share size={14} className="text-sky-300" />
              <span>Share at the bottom of Safari</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-white/8 flex items-center
                               justify-center text-[10px] font-mono">2</span>
              Choose
              <Plus size={14} className="text-emerald-300" />
              <span>Add to Home Screen</span>
            </li>
          </ol>
          <button
            onClick={dismiss}
            className="mt-3 w-full h-9 rounded-full text-[10px] tracking-[0.22em]
                       uppercase font-semibold border border-white/20
                       text-white/80 hover:bg-white/5 transition-colors"
            data-testid="install-pwa-ios-got-it"
          >
            Got it
          </button>
        </div>
      ) : (
        <div className="mt-4 flex gap-2">
          <button
            onClick={install}
            disabled={!deferredPrompt}
            className="flex-1 h-10 rounded-full text-[10px] tracking-[0.22em]
                       uppercase font-semibold bg-white text-[#0B0B0D]
                       hover:bg-white/90 transition-colors inline-flex
                       items-center justify-center gap-2 disabled:opacity-50"
            data-testid="install-pwa-install-btn"
          >
            <Download size={13} /> Install
          </button>
          <button
            onClick={dismiss}
            className="h-10 px-4 rounded-full text-[10px] tracking-[0.22em]
                       uppercase font-semibold border border-white/20
                       text-white/70 hover:bg-white/5 transition-colors
                       inline-flex items-center gap-2"
            data-testid="install-pwa-not-now-btn"
          >
            Not now
          </button>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-white/8 flex items-center
                      gap-2 text-[9px] tracking-[0.22em] uppercase text-white/40">
        <Smartphone size={10} /> Works on Android · iOS · Desktop
      </div>
    </div>
  );
}
